import { VideoData } from '@lumen5/luminary';

import { colord } from 'colord';
import { resolveThemeRefString } from 'js/src/libs/theme-ref-palette';

type AnyRecord = Record<string, unknown>;

export type ChartDisplayedColor = {
    /**
     * Stable identity for the slot, used as the React key. Editing a palette slot rewrites its
     * `ref` (a template reference becomes a fixed hex), so keying off `ref` would remount the
     * swatch — and close its open color picker — on every change. The slot's position is stable.
     */
    key: string;
    /** The current color reference in the config, e.g. `themeRef_colors_3` or `#ff0000`. */
    ref: string;
    /** Resolved hex color for display. */
    hex: string;
};

/** A palette entry is either a template reference or a literal CSS color (a user override). */
function isPaletteColor(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    return parseThemeColorIndex(value) !== null || colord(value).isValid();
}

function parseThemeColorIndex(value: unknown): number | null {
    if (typeof value !== 'string') return null;
    if (!VideoData.isTemplateReference(value)) return null;
    if (!value.includes('_colors_')) return null;
    const idx = VideoData.getTemplateIndex(value);
    return Number.isInteger(idx) && idx >= 0 ? idx : null;
}

function walkColorStrings(node: unknown, visit: (value: string) => void): void {
    if (typeof node === 'string') {
        visit(node);
        return;
    }
    if (!node || typeof node !== 'object') return;
    if ('$animate' in (node as AnyRecord)) return;
    if (Array.isArray(node)) {
        for (const child of node) walkColorStrings(child, visit);
        return;
    }
    for (const key of Object.keys(node as AnyRecord)) {
        walkColorStrings((node as AnyRecord)[key], visit);
    }
}

function collectUsedThemeRefIndices(echartsConfig: AnyRecord): number[] {
    const set = new Set<number>();
    walkColorStrings(echartsConfig, (value) => {
        const idx = parseThemeColorIndex(value);
        if (idx !== null) set.add(idx);
    });
    return Array.from(set).sort((a, b) => a - b);
}

type ColorGroup = 'visualization' | 'text';

function classifyColorGroup(path: string[]): ColorGroup {
    for (let i = path.length - 1; i >= 0; i--) {
        const key = path[i];
        if (key === 'itemStyle' || key === 'lineStyle' || key === 'areaStyle') {
            return 'visualization';
        }
        if (
            key === 'label' ||
            key === 'endLabel' ||
            key === 'textStyle' ||
            key === 'subtextStyle' ||
            key === 'axisLabel' ||
            key === 'axisName'
        ) {
            return 'text';
        }
        // graphic[*] is overwhelmingly text in our chart configs (captions, highlight
        // numbers). If we ever add non-text graphics with themeRef fills, this rule will
        // need to inspect graphic.children[*].type instead.
        if (key === 'graphic') {
            return 'text';
        }
    }
    return 'visualization';
}

function walkColorStringsWithPath(
    node: unknown,
    visit: (value: string, path: string[]) => void,
    path: string[] = [],
): void {
    if (typeof node === 'string') {
        visit(node, path);
        return;
    }
    if (!node || typeof node !== 'object') return;
    if ('$animate' in (node as AnyRecord)) return;
    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
            path.push(String(i));
            walkColorStringsWithPath(node[i], visit, path);
            path.pop();
        }
        return;
    }
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        walkColorStringsWithPath((node as AnyRecord)[key], visit, path);
        path.pop();
    }
}

function remapChartColors(node: unknown, mapping: Map<string, string>): unknown {
    if (typeof node === 'string') {
        return mapping.get(node) ?? node;
    }
    if (!node || typeof node !== 'object') return node;
    if ('$animate' in (node as AnyRecord)) return node;
    if (Array.isArray(node)) {
        return node.map((child) => remapChartColors(child, mapping));
    }
    const result: AnyRecord = {};
    for (const key of Object.keys(node as AnyRecord)) {
        result[key] = remapChartColors((node as AnyRecord)[key], mapping);
    }
    return result;
}

/**
 * Returns one entry per distinct palette color the chart pulls from. A palette color is either
 * a template reference (`themeRef_colors_N`) or a literal hex the user has set as an override —
 * once overridden, a slot stops being a template reference but must remain an editable swatch.
 *
 * Slots come from the top-level `color` array (the canonical chart palette, in order), plus any
 * template references used elsewhere in the config but not present in that array. Literal colors
 * authored outside the palette (e.g. `itemStyle.borderColor`, gridlines, axis labels) are not
 * treated as slots.
 */
export function getChartDisplayedColors(
    echartsConfig: AnyRecord,
    templateColors: number[],
): ChartDisplayedColor[] {
    const result: ChartDisplayedColor[] = [];
    const seenRefs = new Set<string>();

    const addRef = (ref: string, key: string): void => {
        if (seenRefs.has(ref)) return;
        seenRefs.add(ref);
        result.push({ key, ref, hex: resolveThemeRefString(ref, templateColors) });
    };

    const palette = echartsConfig.color;
    if (Array.isArray(palette)) {
        palette.forEach((entry, i) => {
            if (isPaletteColor(entry)) addRef(entry, `palette-${i}`);
        });
    }

    // Template references used elsewhere (e.g. series itemStyle) that aren't listed in the
    // palette array still represent slots; key them by ref since they have no array position.
    collectUsedThemeRefIndices(echartsConfig).forEach((i) => {
        const ref = VideoData.templateColorByIndex(i);
        addRef(ref, ref);
    });

    return result;
}

/**
 * Remap every occurrence of `previousRef` in the config to `newColor`. If the previous value
 * was a template color reference and the user picks a hex, that slot in the chart becomes a
 * fixed hex override and no longer follows template palette changes.
 */
export function setChartPaletteColor(
    echartsConfig: AnyRecord,
    previousRef: string,
    newColor: string,
): AnyRecord {
    if (!previousRef || previousRef === newColor) return echartsConfig;
    return remapChartColors(echartsConfig, new Map([[previousRef, newColor]])) as AnyRecord;
}

function fisherYatesInPlace<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
}

/**
 * A palette slot value is either a template reference (anywhere in the config) or a literal
 * color the user pinned into the top-level `color` array by overriding a swatch. Literals
 * elsewhere (borders, gridlines, default text colors) are NOT palette slots — mirror the
 * slot rules in `getChartDisplayedColors` so shuffle moves exactly the swatches the user sees.
 */
function isPaletteSlotValueAt(value: string, path: string[]): boolean {
    if (parseThemeColorIndex(value) !== null) return true;
    if (path.length === 2 && path[0] === 'color') return colord(value).isValid();
    return false;
}

/**
 * Pick a permutation of the `used` palette slot values that prefers bringing in fresh, unused
 * template colors first, falling back to permuting among the values already in use (which
 * includes user hex overrides) when no fresh template slots remain. Returns a Map from old
 * value → new value. When the only viable result is an identity mapping (e.g. a single-color
 * group with no other slots available), returns an empty map so callers can no-op.
 */
function buildGroupShuffleMapping(used: string[], templateColorCount: number): Map<string, string> {
    if (used.length === 0) return new Map();

    const usedSet = new Set(used);
    const unusedTemplateRefs: string[] = [];
    for (let i = 0; i < templateColorCount; i++) {
        const ref = VideoData.templateColorByIndex(i);
        if (!usedSet.has(ref)) unusedTemplateRefs.push(ref);
    }

    for (let attempt = 0; attempt < 8; attempt++) {
        const freshColors = [...unusedTemplateRefs];
        const recycledColors = [...used];
        fisherYatesInPlace(freshColors);
        fisherYatesInPlace(recycledColors);
        const pool = [...freshColors, ...recycledColors].slice(0, used.length);

        const candidate = new Map<string, string>();
        let anyChanged = false;
        for (let i = 0; i < used.length; i++) {
            const from = used[i];
            const to = pool[i];
            candidate.set(from, to);
            if (from !== to) anyChanged = true;
        }
        if (anyChanged) return candidate;
    }
    return new Map();
}

function remapColorsByGroup(
    node: unknown,
    mappingByGroup: Record<ColorGroup, Map<string, string>>,
    path: string[],
): unknown {
    if (typeof node === 'string') {
        const next = mappingByGroup[classifyColorGroup(path)].get(node);
        return next ?? node;
    }
    if (!node || typeof node !== 'object') return node;
    if ('$animate' in (node as AnyRecord)) return node;
    if (Array.isArray(node)) {
        return node.map((child, i) => {
            path.push(String(i));
            const result = remapColorsByGroup(child, mappingByGroup, path);
            path.pop();
            return result;
        });
    }
    const result: AnyRecord = {};
    for (const key of Object.keys(node as AnyRecord)) {
        path.push(key);
        result[key] = remapColorsByGroup((node as AnyRecord)[key], mappingByGroup, path);
        path.pop();
    }
    return result;
}

export function shuffleChartPalette(
    echartsConfig: AnyRecord,
    templateColorCount: number,
): AnyRecord {
    if (templateColorCount <= 0) return echartsConfig;

    const usedByGroup: Record<ColorGroup, Set<string>> = {
        visualization: new Set(),
        text: new Set(),
    };

    walkColorStringsWithPath(echartsConfig, (value, path) => {
        if (!isPaletteSlotValueAt(value, path)) return;
        usedByGroup[classifyColorGroup(path)].add(value);
    });

    if (usedByGroup.visualization.size === 0 && usedByGroup.text.size === 0) {
        return echartsConfig;
    }

    const mappingByGroup: Record<ColorGroup, Map<string, string>> = {
        visualization: buildGroupShuffleMapping(
            Array.from(usedByGroup.visualization).sort(),
            templateColorCount,
        ),
        text: buildGroupShuffleMapping(Array.from(usedByGroup.text).sort(), templateColorCount),
    };

    const anyChanged =
        Array.from(mappingByGroup.visualization.entries()).some(([k, v]) => k !== v) ||
        Array.from(mappingByGroup.text.entries()).some(([k, v]) => k !== v);
    if (!anyChanged) return echartsConfig;

    return remapColorsByGroup(echartsConfig, mappingByGroup, []) as AnyRecord;
}
